import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { ToastData, ToastType } from '../types/notifications';

// Maximum number of toasts to show at once
const MAX_TOASTS = 3;

// Toast state
interface ToastState {
  toasts: ToastData[];
}

// Toast actions
type ToastAction =
  | { type: 'ADD_TOAST'; payload: ToastData }
  | { type: 'REMOVE_TOAST'; payload: string };

const toastReducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case 'ADD_TOAST':
      const newToasts = [action.payload, ...state.toasts];
      // Keep only the latest MAX_TOASTS toasts
      return {
        ...state,
        toasts: newToasts.slice(0, MAX_TOASTS),
      };
    
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload),
      };
    
    default:
      return state;
  }
};

// Context type for toast notifications
interface ToastContextType {
  toasts: ToastData[];
  showToast: (message: string, type: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(toastReducer, {
    toasts: [],
  });

  const showToast = (message: string, type: ToastType, duration?: number) => {
    const toast: ToastData = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      message,
      type,
      duration,
    };
    dispatch({ type: 'ADD_TOAST', payload: toast });
  };

  const removeToast = (id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  };

  return (
    <ToastContext.Provider
      value={{
        toasts: state.toasts,
        showToast,
        removeToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

export const useToasts = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToasts must be used within a ToastProvider');
  }
  return context;
};
